import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { mapDisputeTypeApiToUi, mapDisputeCategoryApiToOption, type DisputeCategoryOption } from "@/lib/mappers/dispute-types";
import { generateMockDisputeTypes } from "@/lib/mock-data/dispute-types-mock-data";
import type { DisputeType } from "@/types/dispute-types";
import type {
  DisputeTypeApiResponse,
  DisputeCategoryApiResponse,
  CreateDisputeTypeRequest,
  UpdateDisputeTypeRequest,
  CreateDisputeTypeResponse,
  UpdateDisputeTypeResponse,
} from "@/types/api/dispute-types";

const mockDisputeTypes = generateMockDisputeTypes();

const mockCategories: DisputeCategoryOption[] = [
  { id: 1, code: "billing", name: "Billing" },
  { id: 2, code: "service", name: "Service" },
  { id: 3, code: "payment", name: "Payment" },
  { id: 4, code: "contract", name: "Contract" },
  { id: 5, code: "other", name: "Other" },
];

export function useDisputeTypesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeType[], Error>(
    ["dispute-types-settings"],
    async ({ signal }) => {
      const response = await apiGet<DisputeTypeApiResponse[]>("/api/v1/settings/dispute-types", undefined, signal);
      return response.map(x => mapDisputeTypeApiToUi(x));
    },
    mockDisputeTypes,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeTypeById(disputeTypeId: number | string | null) {
  const { apiGet } = useApi();
  const mockDisputeType = mockDisputeTypes.find(x => x.id === String(disputeTypeId)) ?? null;

  return useModeAwareQuery<DisputeType | null, Error>(
    ["dispute-type", disputeTypeId],
    async ({ signal }) => {
      if (!disputeTypeId) return null;
      const response = await apiGet<DisputeTypeApiResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, undefined, signal);
      return mapDisputeTypeApiToUi(response);
    },
    mockDisputeType,
    {
      enabled: !!disputeTypeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeCategories() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeCategoryOption[], Error>(
    ["dispute-categories"],
    async ({ signal }) => {
      const response = await apiGet<DisputeCategoryApiResponse[]>("/api/v1/settings/dispute-types/categories", undefined, signal);
      return response.map(x => mapDisputeCategoryApiToOption(x));
    },
    mockCategories,
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

export function useCreateDisputeType() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreateDisputeTypeResponse, Error, CreateDisputeTypeRequest>(
    async (data) => {
      return apiPost<CreateDisputeTypeResponse>("/api/v1/settings/dispute-types", data);
    },
    async (data) => {
      return {
        id: Date.now(),
        name: data.name,
        description: data.description,
        categoryId: data.categoryId,
        categoryName: undefined,
        isActive: data.isActive ?? true,
        usageCount: 0,
        avgResolutionTime: undefined,
        successRate: undefined,
        createdByUserId: undefined,
        createdByUserName: "Current User",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
      },
    }
  );
}

export function useUpdateDisputeType(disputeTypeId: number | string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeTypeResponse, Error, UpdateDisputeTypeRequest>(
    async (data) => {
      return apiPut<UpdateDisputeTypeResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, data);
    },
    async (data) => {
      return {
        id: Number(disputeTypeId),
        name: data.name ?? "Updated Dispute Type",
        description: data.description,
        categoryId: data.categoryId,
        categoryName: undefined,
        isActive: data.isActive ?? true,
        usageCount: 0,
        avgResolutionTime: undefined,
        successRate: undefined,
        createdByUserId: undefined,
        createdByUserName: "Current User",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", disputeTypeId] });
      },
    }
  );
}

export function useDeleteDisputeType() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, number | string>(
    async (disputeTypeId) => {
      await apiDelete(`/api/v1/settings/dispute-types/${disputeTypeId}`);
    },
    async () => {
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
      },
    }
  );
}

export function useToggleDisputeTypeStatus() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeTypeResponse, Error, { id: number | string; isActive: boolean }>(
    async (data) => {
      return apiPut<UpdateDisputeTypeResponse>(`/api/v1/settings/dispute-types/${data.id}`, { isActive: data.isActive });
    },
    async (data) => {
      return {
        id: Number(data.id),
        name: "Dispute Type",
        description: undefined,
        categoryId: undefined,
        categoryName: undefined,
        isActive: data.isActive,
        usageCount: 0,
        avgResolutionTime: undefined,
        successRate: undefined,
        createdByUserId: undefined,
        createdByUserName: "Current User",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", variables.id] });
      },
    }
  );
}
